// ─── ToolAction — agent-agnostic, normalized description of a tool_call ──────────────
//
// PRINCIPLE (user 2026-06-10): things that DIFFER per agent live in that agent's plugin;
// things that are the SAME across agents are ONE shared piece everyone calls.
//
// A tool_call's `toolName` + `toolInput` shape is AGENT-SPECIFIC (Claude's `Edit`
// {file_path, old_string, new_string} ≠ another agent's `write_file` {path, content}). So the
// MAPPING raw tool_call → ToolAction is per-agent (agents/<name>.ts). The RENDERING of a
// ToolAction (diff card / code card / collapsible) is identical for everyone → it lives once in
// the central renderer (components/turns/ToolCard.tsx). This file is the contract between them:
// the normalized data shape + the per-agent registry + a generic best-effort fallback.

import type { ConversationTurn } from '../api/client'

// Normalized DATA only — no display strings/icons (those are the renderer's job, shared).
export type ToolAction =
  | { kind: 'edit';   file: string; hunks: { old: string; new: string }[] }
  | { kind: 'write';  file: string; content: string }
  | { kind: 'read';   file: string; offset?: number; limit?: number }
  | { kind: 'bash';   command: string; description?: string }
  | { kind: 'search'; engine: 'grep' | 'glob' | 'ls'; query: string; path?: string }
  | { kind: 'fetch';  url: string }
  | { kind: 'websearch'; query: string }
  | { kind: 'todos';  todos: { content: string; status: string }[] }
  | { kind: 'task';   subagent?: string; description: string }
  | { kind: 'generic'; name: string; raw?: string }

// ─── shared interpretation utils (used by every agent mapper) ───────────────────────

export function basename(p: string): string {
  if (!p) return ''
  const parts = p.replace(/\/+$/, '').split('/')
  return parts[parts.length - 1] || p
}

export function clip(s: string, n = 80): string {
  const oneLine = (s ?? '').replace(/\s+/g, ' ').trim()
  return oneLine.length > n ? oneLine.slice(0, n - 1) + '…' : oneLine
}

export function prettyJson(v: unknown): string {
  try { return JSON.stringify(v, null, 2) } catch { return String(v) }
}

export function parseInput(turn: ConversationTurn): Record<string, unknown> | null {
  if (!turn.toolInput) return null
  try {
    const v = JSON.parse(turn.toolInput)
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/** A tool_call → ToolAction mapper for ONE agent. Returns null when this agent's mapper
 *  doesn't recognise the tool (→ caller falls back to the generic mapper). */
export type ToolMapper = (turn: ConversationTurn) => ToolAction | null

// ─── generic fallback — best-effort on COMMON field names ───────────────────────────
// Used when an agent has no mapper yet, or its mapper returns null. Recognises the field
// names most agents share so a brand-new agent still renders something useful before anyone
// writes its dedicated mapper. Agent-SPECIFIC quirks belong in agents/<name>.ts, NOT here.

export function genericToolAction(turn: ConversationTurn): ToolAction | null {
  const name = (turn.toolName || '').trim()
  if (!name) return null
  const input = parseInput(turn) ?? {}
  const s = (k: string): string => (typeof input[k] === 'string' ? (input[k] as string) : '')
  const num = (k: string): number | undefined => (input[k] != null && !Number.isNaN(Number(input[k])) ? Number(input[k]) : undefined)
  const file = s('file_path') || s('path') || s('filename')

  if (s('old_string') || s('new_string')) return { kind: 'edit', file, hunks: [{ old: s('old_string'), new: s('new_string') }] }
  if (Array.isArray(input.edits))         return { kind: 'edit', file, hunks: (input.edits as Array<Record<string, string>>).map(e => ({ old: e.old_string ?? '', new: e.new_string ?? '' })) }
  if ((s('content') || s('file_text')) && file) return { kind: 'write', file, content: s('content') || s('file_text') }
  if (s('command'))                       return { kind: 'bash', command: s('command'), description: s('description') }
  if (s('url'))                           return { kind: 'fetch', url: s('url') }
  if (s('query'))                         return { kind: 'websearch', query: s('query') }
  if (s('pattern'))                       return { kind: 'search', engine: 'grep', query: s('pattern'), path: s('path') }
  if (Array.isArray(input.todos))         return { kind: 'todos', todos: (input.todos as Array<{ content?: string; status?: string }>).map(t => ({ content: t.content ?? '', status: t.status ?? 'pending' })) }
  if (file)                               return { kind: 'read', file, offset: num('offset'), limit: num('limit') }
  return { kind: 'generic', name, raw: prettyJson(input) }
}

// ─── per-agent registry ─────────────────────────────────────────────────────────────
// Add a mapper here when an agent's tool names/shapes differ enough that the generic
// fallback misreads them. Keep each mapper in its own agents/<name>.ts file.

import { claudeToolAction } from './claude'

const REGISTRY: Record<string, ToolMapper> = {
  claude: claudeToolAction,
  // qwen / gemini / codex: fall back to genericToolAction until a dedicated mapper exists.
}

/** Normalize a tool_call for the given agent: try the agent's mapper, else the generic one. */
export function getToolAction(agent: string | null | undefined, turn: ConversationTurn): ToolAction | null {
  const mapper = agent ? REGISTRY[agent] : undefined
  return (mapper && mapper(turn)) || genericToolAction(turn)
}
